import { RulebookListCardStatus } from "@next-platform/client-common-core/rulebook/cards/RulebookListCardStatus";
import { Badge, BadgeSize, Icon, IconSize, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import React, { memo } from "react";

import Trans from "@/components/Trans/Trans";

type StatusPillConfig = {
  icon: IconVariants;
  className: string;
};

const STATUS_PILL_CONFIG: Record<RulebookListCardStatus, StatusPillConfig> = {
  [RulebookListCardStatus.PUBLIC]: {
    icon: IconVariants.GLOBE,
    className: "bg-green-600/15 text-green-600",
  },
  [RulebookListCardStatus.IN_REVIEW]: {
    icon: IconVariants.CLOCK,
    className: "bg-blue-3/60 text-blue-11",
  },
  [RulebookListCardStatus.NEEDS_CHANGES]: {
    icon: IconVariants.ALERT_TRIANGLE,
    className: "bg-yellow-3 text-yellow-11",
  },
  [RulebookListCardStatus.PRIVATE]: {
    icon: IconVariants.LOCK,
    className: "bg-gray-5 text-gray-11",
  },
  [RulebookListCardStatus.DRAFT]: {
    icon: IconVariants.FILE,
    className: "bg-gray-5 text-gray-11",
  },
};

const renderStatusLabel = (status: RulebookListCardStatus) => {
  switch (status) {
    case RulebookListCardStatus.PUBLIC:
      return <Trans i18nKey="rulebook:card.status.public" defaults="Public" />;
    case RulebookListCardStatus.IN_REVIEW:
      return <Trans i18nKey="rulebook:card.status.inReview" defaults="In review" />;
    case RulebookListCardStatus.NEEDS_CHANGES:
      return <Trans i18nKey="rulebook:card.status.needsChanges" defaults="Needs changes" />;
    case RulebookListCardStatus.PRIVATE:
      return <Trans i18nKey="rulebook:card.status.private" defaults="Private" />;
    case RulebookListCardStatus.DRAFT:
      return <Trans i18nKey="rulebook:card.status.draft" defaults="Draft" />;
    default:
      return null;
  }
};

type RulebookListCardStatusPillProps = {
  status: RulebookListCardStatus;
  "data-testid"?: string;
};

const RulebookListCardStatusPill = ({ status, "data-testid": testId = "RulebookListCardStatusPill" }: RulebookListCardStatusPillProps) => {
  const config = STATUS_PILL_CONFIG[status];
  const label = renderStatusLabel(status);

  return (
    <Badge
      showDot={false}
      size={BadgeSize.SMALL}
      className={`inline-flex h-[22px] gap-1 rounded-[5px] px-1.5 ${config.className}`}
      data-status={status}
      data-testid={testId}
    >
      <Icon icon={config.icon} size={IconSize.XSMALL} />
      <Text as="span" variant={TextVariants.LABEL_SMALL_BOLD}>
        {label}
      </Text>
    </Badge>
  );
};

export default memo(RulebookListCardStatusPill);
